import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLES      = ['All', 'buyer', 'seller', 'driver', 'admin']
const STATUS     = ['All', 'Active', 'Inactive']
const EDIT_ROLES = ['buyer', 'seller', 'driver', 'admin', 'superadmin']

const roleColor = (role) => ({
  buyer:      { bg: '#eff6ff', color: '#1d4ed8' },
  seller:     { bg: '#f0fdf4', color: '#15803d' },
  driver:     { bg: '#fff7ed', color: '#c2410c' },
  admin:      { bg: '#fdf4ff', color: '#7e22ce' },
  superadmin: { bg: '#fef2f2', color: '#b91c1c' },
}[role] || { bg: '#f3f4f6', color: '#374151' })

const statusColor = (status) =>
  status === 1
    ? { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active' }
    : { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Inactive' }

const avatar   = (user) => (user.username || user.phone || '?').charAt(0).toUpperCase()
const avatarBg = (str) => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

// ── Shared styles ─────────────────────────────────────────────────────────────
const selectStyle = {
  border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px',
  fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff',
  cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif",
}
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#111317',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
  fontFamily: "'JetBrains Mono',monospace",
}
const inputStyle = {
  width: '100%', height: 42, borderRadius: 8,
  border: '1px solid #e9e9e4', padding: '0 13px',
  fontSize: 13, fontWeight: 500, color: '#111317',
  outline: 'none', background: '#fff',
  fontFamily: "'Plus Jakarta Sans',sans-serif",
  boxSizing: 'border-box',
}

// ── Pill ──────────────────────────────────────────────────────────────────────
function Pill({ label, bg, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: bg, color, fontSize: 10, fontWeight: 700,
      fontFamily: "'JetBrains Mono',monospace",
      padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>{label}</span>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 5, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

// ── Edit Profile Modal ────────────────────────────────────────────────────────
function EditModal({ user, onClose, onSaved }) {
  const [form,    setForm]    = useState({
    username: user.username || '',
    email:    user.email    || '',
    role:     user.role     || 'buyer',
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      // Update username + email
      const res = await api.put(`/user/update/admin/users/${user.user_id}`, {
        username: form.username || null,
        email:    form.email    || null,
      })

      // Update role separately if it changed
      if (form.role !== user.role) {
        await api.patch(`/user/update/admin/users/${user.user_id}/role`, { role: form.role })
      }

      onSaved({ ...user, ...res.data.user, role: form.role })
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />

      {/* Modal box */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: 460, background: '#fff', borderRadius: 14,
        zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        animation: 'popIn 0.2s ease',
      }}>
        <style>{`
          @keyframes popIn {
            from { opacity:0; transform:translate(-50%,-48%) scale(0.97) }
            to   { opacity:1; transform:translate(-50%,-50%) scale(1) }
          }
        `}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Edit Profile</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>
              #{user.user_id} · {user.phone}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div>
              <label style={labelStyle}>Username</label>
              <input
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="Enter username"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="Enter email address"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Role</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {EDIT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Phone — read only */}
            <div>
              <label style={labelStyle}>
                Phone <span style={{ color: '#9a9a94', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(cannot be changed)</span>
              </label>
              <input
                value={user.phone}
                disabled
                style={{ ...inputStyle, background: '#f9f9f7', color: '#9a9a94', cursor: 'not-allowed' }}
              />
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '10px 14px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                ⚠ {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
            >Cancel</button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1, height: 42, borderRadius: 8, border: 'none',
                background: loading ? '#f0c4b4' : '#e8440a',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                boxShadow: loading ? 'none' : '0 2px 8px rgba(232,68,10,0.25)',
              }}
            >{loading ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </>
  )
}


// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ onClose, onCreated }) {
  const [form,    setForm]    = useState({ username: '', phone: '', email: '', password: '', role: 'buyer' })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      const res = await api.post('/user/update/admin/users', form)
      onCreated(res.data.user)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user')
    } finally { setLoading(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: 460, background: '#fff', borderRadius: 14, zIndex: 301,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease',
      }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Create User</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>Add a new user to the platform manually.</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 15 }}>

            {/* Phone — required, primary identifier */}
            <div>
              <label style={labelStyle}>Phone Number *</label>
              <input required value={form.phone} onChange={e => set('phone', e.target.value)}
                placeholder="+250 78X XXX XXX"
                style={inputStyle} />
              <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4 }}>Primary login identifier. Cannot be changed later.</div>
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>Password *</label>
              <input required type="password" value={form.password} onChange={e => set('password', e.target.value)}
                placeholder="Set a password"
                style={inputStyle} />
            </div>

            {/* Username + Role in a grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Username</label>
                <input value={form.username} onChange={e => set('username', e.target.value)}
                  placeholder="e.g. john_doe"
                  style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Role</label>
                <select value={form.role} onChange={e => set('role', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}>
                  {EDIT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email <span style={{ color: '#9a9a94', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>(optional)</span></label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="user@example.com"
                style={inputStyle} />
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '10px 14px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                ⚠ {error}
              </div>
            )}
          </div>

          <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose}
              style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: loading ? 'none' : '0 2px 8px rgba(232,68,10,0.25)' }}>
              {loading ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── User Detail Drawer ────────────────────────────────────────────────────────
function UserDrawer({ user, onClose, onStatusToggle, onSuspendToggle, onEditOpen }) {
  if (!user) return null
  const s = statusColor(user.status)
  const r = roleColor(user.role)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
        background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.1)',
        animation: 'slideIn 0.22s ease',
      }}>
        <style>{`@keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: avatarBg(user.phone || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#111317', flexShrink: 0 }}>
            {avatar(user)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{user.username || 'No username'}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{user.phone}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <Pill label={user.role} {...r} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
            </span>
            {user.is_suspended && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#450a0a', color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
                Suspended
              </span>
            )}
          </div>

          {[
            ['User ID',    user.user_id],
            ['Phone',      user.phone],
            ['Email',      user.email    || '—'],
            ['Username',   user.username || '—'],
            ['Role',       user.role],
            ['Shop ID',    user.shop_id  || '—'],
            ['Joined',     fmt(user.created_at)],
            ['Last Login', fmt(user.last_login)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid #f3f3f0' }}>
              <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
              <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ padding: 20, borderTop: '1px solid #e9e9e4', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => onSuspendToggle(user)}
            style={{ height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: user.is_suspended ? '#f0fdf4' : '#450a0a', color: user.is_suspended ? '#15803d' : '#fff', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {user.is_suspended ? 'Unsuspend User' : 'Suspend User'}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => onStatusToggle(user)}
              style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: user.status === 1 ? '#fef2f2' : '#f0fdf4', color: user.status === 1 ? '#b91c1c' : '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {user.status === 1 ? 'Deactivate User' : 'Reactivate User'}
            </button>
            <button
              onClick={() => onEditOpen(user)}
              style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              Edit Profile
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Users() {
  const [users,      setUsers]      = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState('')
  const [search,     setSearch]     = useState('')
  const [roleFilter, setRoleFilter] = useState('All')
  const [statFilter, setStatFilter] = useState('All')
  const [page,       setPage]       = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [selected,   setSelected]   = useState(null)  // drawer
  const [editing,    setEditing]    = useState(null)  // edit modal
  const [creating,   setCreating]   = useState(false) // create modal
  const [stats,      setStats]      = useState({ total: 0, active: 0, inactive: 0, new: 0 })

  const PER_PAGE = 15

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search               && { search }),
        ...(roleFilter !== 'All' && { role: roleFilter }),
        ...(statFilter !== 'All' && { status: statFilter === 'Active' ? 1 : 0 }),
      })
      const res = await api.get(`/user/update/admin/users?${params}`)
      setUsers(res.data.users      || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalUsers(res.data.total      || 0)
      setStats(res.data.stats      || { total: 0, active: 0, inactive: 0, new: 0 })
    } catch {
      setError('Failed to load users')
      const mock = Array.from({ length: 12 }, (_, i) => ({
        user_id: i + 1, username: i % 3 === 0 ? null : `user_${i + 1}`,
        phone: `078${String(i + 1).padStart(7, '0')}`,
        email: i % 2 === 0 ? `user${i+1}@example.com` : null,
        role: ['buyer','seller','driver','admin'][i % 4],
        status: i % 5 === 0 ? 0 : 1, shop_id: i % 4 === 1 ? i * 10 : null,
        created_at: new Date(Date.now() - i * 86400000 * 7).toISOString(),
        last_login: new Date(Date.now() - i * 86400000).toISOString(),
      }))
      setUsers(mock)
      setTotalPages(3)
      setTotalUsers(42)
      setStats({ total: 42, active: 38, inactive: 4, new: 7 })
    } finally {
      setLoading(false)
    }
  }, [page, search, roleFilter, statFilter])

  useEffect(() => { fetchUsers() }, [fetchUsers])
  useEffect(() => { setPage(1)  }, [search, roleFilter, statFilter])

  const handleStatusToggle = async (user) => {
    const newStatus = user.status === 1 ? 0 : 1
    try {
      await api.patch(`/user/update/admin/users/${user.user_id}/status`, { status: newStatus })
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, status: newStatus } : u))
      if (selected?.user_id === user.user_id) setSelected(prev => ({ ...prev, status: newStatus }))
    } catch {
      alert('Failed to update user status')
    }
  }

  // Suspend is distinct from status above: it blocks login outright (even
  // an already-open session gets killed immediately) and cascades to
  // suspend any shop/creator profile this user runs. Un-suspending only
  // restores login — shop/creator must be reactivated separately.
  const handleSuspendToggle = async (user) => {
    const suspending = !user.is_suspended
    if (suspending && !window.confirm(`Suspend ${user.username || user.phone}? This blocks their login immediately and also suspends any shop or creator profile they run.`)) return
    try {
      const res = await api.patch(`/user/update/admin/users/${user.user_id}/suspend`, { suspended: suspending })
      const isSuspended = res.data?.user?.is_suspended ?? suspending
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, is_suspended: isSuspended } : u))
      if (selected?.user_id === user.user_id) setSelected(prev => ({ ...prev, is_suspended: isSuspended }))
    } catch {
      alert('Failed to update suspension')
    }
  }

  const handleSaved = (updatedUser) => {
    setUsers(prev => prev.map(u => u.user_id === updatedUser.user_id ? updatedUser : u))
    if (selected?.user_id === updatedUser.user_id) setSelected(updatedUser)
  }

  const handleCreated = (newUser) => {
    setUsers(prev => [newUser, ...prev])
    setTotalUsers(c => c + 1)
    setStats(s => ({ ...s, total: s.total + 1, active: s.active + 1 }))
  }

  const filtered = users.filter(u =>
    !search || [u.username, u.phone, u.email].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>User Management</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>View, search and manage all platform users.</div>
        </div>
        <button onClick={() => setCreating(true)}
          style={{ height: 40, padding: '0 20px', borderRadius: 9, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(232,68,10,0.25)', flexShrink: 0 }}>
          + Create User
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Users"    value={stats.total.toLocaleString()}    sub="All time"     />
        <StatCard label="Active"         value={stats.active.toLocaleString()}   sub="Status = 1"   accent="#15803d" />
        <StatCard label="Inactive"       value={stats.inactive.toLocaleString()} sub="Status = 0"   accent="#b91c1c" />
        <StatCard label="New This Month" value={stats.new.toLocaleString()}       sub="Last 30 days" accent="#1d4ed8" />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone or email…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
          />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={selectStyle}>
          {ROLES.map(r => <option key={r}>{r}</option>)}
        </select>
        <select value={statFilter} onChange={e => setStatFilter(e.target.value)} style={selectStyle}>
          {STATUS.map(s => <option key={s}>{s}</option>)}
        </select>
        <button onClick={fetchUsers} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalUsers.toLocaleString()} users</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>
          ⚠ {error} — showing mock data
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1.2fr 0.9fr 0.9fr 1fr 0.7fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['User','Phone','Email','Role','Status','Joined','Actions'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading users…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No users found.</div>
        ) : (
          filtered.map((user, i) => {
            const s  = statusColor(user.status)
            const r  = roleColor(user.role)
            const bg = avatarBg(user.phone || '')
            return (
              <div
                key={user.user_id}
                onClick={() => setSelected(user)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1.2fr 0.9fr 0.9fr 1fr 0.7fr', padding: '13px 20px', borderBottom: i < filtered.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#111317', flexShrink: 0 }}>{avatar(user)}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111317' }}>
                      {user.username || <span style={{ color: '#9a9a94', fontStyle: 'italic' }}>No username</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{user.user_id}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{user.phone}</div>
                <div style={{ fontSize: 12, color: user.email ? '#111317' : '#9a9a94', fontStyle: user.email ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email || 'No email'}</div>
                <div><Pill label={user.role} {...r} /></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
                  </span>
                  {user.is_suspended && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', background: '#450a0a', color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase' }}>
                      Suspended
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(user.created_at)}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelected(user)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  <button onClick={() => handleStatusToggle(user)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: user.status === 1 ? '#fef2f2' : '#f0fdf4', color: user.status === 1 ? '#b91c1c' : '#15803d', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {user.status === 1 ? 'Deactivate' : 'Reactivate'}
                  </button>
                  <button onClick={() => handleSuspendToggle(user)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: user.is_suspended ? '#f0fdf4' : '#450a0a', color: user.is_suspended ? '#15803d' : '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}           onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages}  onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Drawer */}
      <UserDrawer
        user={selected}
        onClose={() => setSelected(null)}
        onStatusToggle={handleStatusToggle}
        onSuspendToggle={handleSuspendToggle}
        onEditOpen={(user) => { setEditing(user); setSelected(null) }}
      />

      {/* Edit Modal */}
      {editing && (
        <EditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Create User Modal */}
      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

// ── Pagination button ─────────────────────────────────────────────────────────
function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}